import {useQuery} from "@apollo/client"
import React from "react"
import {
  GetNavigationThreadsDocument,
  GetNavigationThreadsQuery,
  GetNavigationThreadsQueryVariables,
  OrderByDirection,
} from "@/gql/graphql"
import {Profile, ThreadWithProfiles} from "./shared"

// Utility: Convert a date to a formatted month string.
export const getMonthFromDate = (date: any): string => {
  const dateObj = new Date(date)
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ]
  const month = dateObj.getMonth() + 1
  const year = dateObj.getFullYear()
  return `${monthNames[month - 1]} ${year}`
}

// Utility: Group threads by month and sort them.
export const getThreadsByMonthMap = (
  threads: ThreadWithProfiles[],
  sortBy: "created_at" | "last_message_timestamp" = "created_at",
  order: "asc" | "desc" = "desc"
): Map<string, ThreadWithProfiles[]> => {
  const monthThreads = new Map<string, ThreadWithProfiles[]>()
  threads.forEach((thread) => {
    // Use last_message_timestamp instead of created_at
    const monthYear = getMonthFromDate(
      thread.last_message_timestamp || thread.created_at
    )
    if (!monthThreads.has(monthYear)) {
      monthThreads.set(monthYear, [])
    }
    monthThreads.get(monthYear)?.push(thread)
  })

  // Sort threads within each month.
  monthThreads.forEach((threads) => {
    threads.sort((a, b) => {
      const aValue = new Date(a[sortBy]).getTime()
      const bValue = new Date(b[sortBy]).getTime()
      return order === "asc" ? aValue - bValue : bValue - aValue
    })
  })

  return monthThreads
}

// Transform raw GraphQL data into our desired thread shape.
function sanitizeResponse(
  data: GetNavigationThreadsQuery
): ThreadWithProfiles[] {
  console.debug("🔍 Sanitizing thread data", {rawData: data})

  const threads = data.threadsCollection?.edges.map((edge) => edge.node)
  if (!threads) {
    console.error("❌ Thread data missing or invalid in sanitizeResponse")
    return []
  }

  return threads
    .map((thread): ThreadWithProfiles | null => {
      // console.debug("🧵 Thread:", {threadId: thread.thread_uid})
      const profiles =
        thread?.message_groups?.group_participantsCollection?.edges.map(
          (edge) => edge.node.profiles
        )
      if (!profiles) {
        console.error("❌ Profiles missing for thread:", thread.thread_uid)
        return null
      }
      const firstMessageSubject =
        thread.thread_messagesCollection?.edges[0]?.node?.messages?.subject

      // Get all message sensitivities and find the maximum
      const messageSensitivities = thread.thread_messagesCollection?.edges
        .map((edge) => edge.node?.messages?.sensitivity)
        .filter(
          (sensitivity) => sensitivity !== undefined && sensitivity !== null
        ) as number[]

      const maxSensitivity =
        messageSensitivities && messageSensitivities.length > 0
          ? Math.max(...messageSensitivities)
          : undefined

      return {
        thread_uid: thread.thread_uid,
        created_at: thread.created_at,
        title: firstMessageSubject,
        description: thread.description,
        last_message_timestamp: thread.last_message_timestamp,
        profiles: profiles as Profile[],
        sensitivity: maxSensitivity,
      }
    })
    .filter((thread): thread is ThreadWithProfiles => thread !== null)
}

interface UseGetNavigationThreadsReturn {
  loading: boolean
  error: any
  data: ThreadWithProfiles[]
  monthThreads: Map<string, ThreadWithProfiles[]>
  hasNextPage: boolean
  loadMore: () => void
}

// Final hook with pagination
export function useGetNavigationThreads(
  itemsPerPage: number = 30
): UseGetNavigationThreadsReturn {
  const {data, loading, error, fetchMore} = useQuery<
    GetNavigationThreadsQuery,
    GetNavigationThreadsQueryVariables
  >(GetNavigationThreadsDocument, {
    variables: {
      first: itemsPerPage,
      after: null,
      orderBy: [
        {
          last_message_timestamp: OrderByDirection.DescNullsLast,
        },
      ],
    },
    notifyOnNetworkStatusChange: true,
    onError: (error) => {
      console.error("❌ Query failed:", error.message)
    },
  })

  const threadsCollection = data?.threadsCollection
  const pageInfo = threadsCollection?.pageInfo

  const loadMore = React.useCallback(() => {
    if (!pageInfo?.hasNextPage || loading) {
      return
    }

    fetchMore({
      variables: {
        after: pageInfo.endCursor,
        first: itemsPerPage,
      },
      updateQuery: (previousResult, {fetchMoreResult}) => {
        if (!fetchMoreResult?.threadsCollection) {
          return previousResult
        }

        const newEdges = fetchMoreResult.threadsCollection.edges
        const newPageInfo = fetchMoreResult.threadsCollection.pageInfo

        return {
          ...previousResult,
          threadsCollection: {
            ...previousResult.threadsCollection,
            edges: [
              ...(previousResult.threadsCollection?.edges || []),
              ...newEdges,
            ],
            pageInfo: newPageInfo,
          },
        }
      },
    })
  }, [fetchMore, pageInfo, loading, itemsPerPage])

  const allSanitizedThreads = React.useMemo(() => {
    return data ? sanitizeResponse(data) : []
  }, [data])

  const monthThreads = React.useMemo(() => {
    return getThreadsByMonthMap(allSanitizedThreads, "last_message_timestamp")
  }, [allSanitizedThreads])

  return {
    loading,
    error,
    data: allSanitizedThreads,
    monthThreads,
    hasNextPage: pageInfo?.hasNextPage ?? false,
    loadMore,
  }
}
